import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Empty,
  Modal,
  Skeleton,
  Tabs,
  Tooltip,
  message,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  MoreOutlined,
  ArrowLeftOutlined,
  BarChartOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Navigate, useSearchParams } from 'react-router-dom';
import OrderSearch from '../components/OrderSearch';
import OrderTable from '../components/OrderTable';
import ExcelUpload from '../components/ExcelUpload';
import PointsDisplay from '../components/PointsDisplay';
import BatchOrderUpdate from '../components/BatchOrderUpdateFixed';
import BatchOrderReset from '../components/BatchOrderResetFixed';
import {
  orderApi,
  statisticsApi,
  accountPointsApi,
  orderStatusUpdateApi,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrderWorkspace } from '../contexts/OrderWorkspaceContext';
import OrderActionReceipt from '../components/OrderActionReceipt';
import { readOrderQuery, writeOrderQuery } from '../utils/orderQuery';
import { ORDER_STATUSES, getLogisticsStatus } from '../utils/orderPresentation';

const statusViews = [
  { key: 'all', label: '全部订单' },
  { key: '0', label: '待审核', codes: [0] },
  { key: 'exceptions', label: '异常', codes: [92, 99] },
  { key: '1', label: '待拍单', codes: [1] },
  { key: '2', label: '已拍单', codes: [2] },
  { key: '91', label: '已取消', codes: [91] },
];
const OrderWorkspace = () => {
  const { user } = useAuth();
  const {
    rememberQuery,
    expandedColumns,
    setExpandedColumns,
    receipt,
    recordReceipt,
  } = useOrderWorkspace();
  const [urlParams, setUrlParams] = useSearchParams();
  const query = useMemo(() => readOrderQuery(urlParams), [urlParams]);
  const view = ['upload', 'statistics'].includes(urlParams.get('view'))
    ? urlParams.get('view')
    : 'query';
  const [result, setResult] = useState({ orders: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [action, setAction] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const deleteLock = useRef(false);
  const [pointsBalance, setPointsBalance] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const exportLock = useRef(false);
  const [statistics, setStatistics] = useState(null);
  const [statisticsError, setStatisticsError] = useState('');
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  useEffect(() => {
    rememberQuery(query);
  }, [query, rememberQuery]);
  const setQuery = useCallback(
    (next) => {
      setSelectedOrders([]);
      setUrlParams(writeOrderQuery(next, view));
    },
    [view, setUrlParams],
  );
  const changeView = (next) => {
    setSelectedOrders([]);
    setUrlParams(writeOrderQuery(query, next));
  };
  const refresh = () => {
    setSelectedOrders([]);
    setRevision((value) => value + 1);
  };

  useEffect(() => {
    if (view !== 'query') return undefined;
    let current = true;
    setLoading(true);
    setError('');
    setSelectedOrders([]);
    orderApi
      .queryOrders({ ...query, username: user?.username })
      .then((response) => {
        if (!response.data || !Array.isArray(response.data.orders))
          throw new Error(
            response.data?.message || '订单返回结果不完整，请重试',
          );
        if (!current) return;
        // A missing or undercounted total must not hide orders already returned by the query.
        const minimumTotal = response.data.orders.length
          ? (query.pageNum - 1) * query.pageSize + response.data.orders.length
          : 0;
        const total = Math.max(response.data.total || 0, minimumTotal);
        const lastPage = Math.max(1, Math.ceil(total / query.pageSize));
        if (query.pageNum > lastPage) {
          setQuery({ ...query, pageNum: lastPage });
          return;
        }
        setResult({ orders: response.data.orders, total });
        setHasLoaded(true);
      })
      .catch((failure) => {
        if (current) setError(failure.message || '订单暂时无法加载');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [query, revision, view, user?.username, setQuery]);

  useEffect(() => {
    let current = true;
    setPointsLoading(true);
    accountPointsApi
      .getAccountPoints(user?.username)
      .then((response) => {
        if (
          response.data?.code !== 200 ||
          response.data.data?.pointsBalance == null
        )
          throw new Error('余额不可用');
        if (current) setPointsBalance(response.data.data.pointsBalance);
      })
      .catch(() => {
        if (current) setPointsBalance(null);
      })
      .finally(() => {
        if (current) setPointsLoading(false);
      });
    return () => {
      current = false;
    };
  }, [revision, user?.username]);

  useEffect(() => {
    if (view === 'upload') return undefined;
    let current = true;
    setStatisticsLoading(true);
    setStatisticsError('');
    statisticsApi
      .getAllOrderStatistics(user?.username)
      .then((response) => {
        if (
          response.data?.code !== 200 ||
          !Array.isArray(response.data.data?.orderStatusCounts)
        )
          throw new Error(response.data?.message || '统计暂时无法加载');
        if (current) setStatistics(response.data.data);
      })
      .catch((failure) => {
        if (current) {
          setStatistics(null);
          setStatisticsError(failure.message);
        }
      })
      .finally(() => {
        if (current) setStatisticsLoading(false);
      });
    return () => {
      current = false;
    };
  }, [view, revision, user?.username]);

  const onTableChange = (pagination, filters, sorter, extra) =>
    setQuery({
      ...query,
      pageNum:
        extra.action === 'sort' || pagination.pageSize !== query.pageSize
          ? 1
          : pagination.current,
      pageSize: pagination.pageSize,
      sortBy:
        extra.action === 'sort'
          ? sorter.order
            ? sorter.field
            : 'createTime'
          : query.sortBy,
      sortDirection:
        extra.action === 'sort'
          ? sorter.order === 'ascend'
            ? 'asc'
            : 'desc'
          : query.sortDirection,
    });
  const onActionSuccess = () => {
    setAction(null);
    refresh();
  };
  const deleteOrders = async () => {
    if (deleteLock.current || !selectedOrders.length) return;
    deleteLock.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      const response = await orderStatusUpdateApi.batchDeleteOrders(
        user?.username,
        selectedOrders.map((order) => order.id),
      );
      if (response.data?.code !== 200)
        throw new Error(response.data?.message || '删除失败');
      message.success('删除请求已完成，正在刷新订单');
      onActionSuccess();
    } catch (failure) {
      setDeleteError(failure.message);
    } finally {
      deleteLock.current = false;
      setDeleting(false);
    }
  };
  const exportOrders = async () => {
    if (exportLock.current) return;
    exportLock.current = true;
    setExporting(true);
    try {
      const { pageNum, pageSize, ...filters } = query;
      const response = await orderApi.exportOrders({
        ...filters,
        username: user?.username,
        fileNamePrefix: '订单导出',
        maxExportSize: 10000,
      });
      const contentType =
        response.headers?.['content-type'] || response.data?.type || '';
      if (contentType.includes('json'))
        throw new Error('导出未完成，请稍后重试');
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '订单导出.xlsx';
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(url);
      }
      message.success('导出文件已生成');
    } catch (failure) {
      message.error(failure.message || '导出失败，请重试');
    } finally {
      exportLock.current = false;
      setExporting(false);
    }
  };
  const statusKey =
    query.statusList
      ?.slice()
      .sort((a, b) => a - b)
      .join(',') === '92,99'
      ? 'exceptions'
      : query.statusList?.join(',') || 'all';
  const validCount = selectedOrders.filter(
    (order) => order.status === 0,
  ).length;
  const queueCount = (codes) => {
    if (
      statisticsLoading ||
      statisticsError ||
      !statistics?.orderStatusCounts.length
    )
      return null;
    // GROUP BY omits zero-count states; an entirely empty response can also mean a backend failure.
    const counts = codes.map((code) => {
      const row = statistics.orderStatusCounts.find(
        (item) => item.statusCode != null && Number(item.statusCode) === code,
      );
      return row ? row.count : 0;
    });
    if (
      counts.some(
        (count) =>
          count == null ||
          count === '' ||
          !Number.isSafeInteger(Number(count)) ||
          Number(count) < 0,
      )
    )
      return null;
    return counts.reduce((sum, count) => sum + Number(count), 0);
  };

  const showReceiptSummary =
    Boolean(receipt) && !selectedOrders.length && !loading && !error;

  return (
    <section className="page-container order-workspace" data-view={view}>
      <header className="page-header">
        <div>
          <h1 className="page-title">
            {view === 'upload'
              ? '导入订单'
              : view === 'statistics'
                ? '订单统计'
                : '订单管理'}
          </h1>
          {view !== 'query' && (
            <p className="page-description">
              {view === 'upload'
                ? '选择文件后主动提交，当前登录期间可返回此处核对结果。'
                : '当前账户的全量状态分布，不受订单列表筛选条件影响。'}
            </p>
          )}
        </div>
        <div className="page-actions">
          {view === 'query' ? (
            <>
              <PointsDisplay
                pointsBalance={pointsBalance}
                loading={pointsLoading}
              />
              <Button
                type={selectedOrders.length ? 'default' : 'primary'}
                icon={<UploadOutlined />}
                onClick={() => changeView('upload')}
              >
                导入订单
              </Button>
            </>
          ) : (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => changeView('query')}
            >
              返回订单
            </Button>
          )}
        </div>
      </header>
      <div
        className={'page-content' + (view === 'query' ? ' order-content' : '')}
      >
        {view === 'query' && (
          <>
            <Tabs
              className="order-status-tabs"
              activeKey={
                statusViews.some((item) => item.key === statusKey)
                  ? statusKey
                  : 'custom'
              }
              items={[
                ...statusViews.map((item) => {
                  const count = item.codes ? queueCount(item.codes) : undefined;
                  return {
                    key: item.key,
                    label: (
                      <span className="queue-label">
                        {item.label}
                        {item.codes && (
                          <span
                            className={
                              'queue-count' +
                              (item.key === 'exceptions' && count > 0
                                ? ' queue-count-alert'
                                : '')
                            }
                          >
                            {count == null ? '—' : count.toLocaleString()}
                          </span>
                        )}
                      </span>
                    ),
                  };
                }),
                ...(!statusViews.some((item) => item.key === statusKey)
                  ? [{ key: 'custom', label: '自定义状态' }]
                  : []),
              ]}
              tabBarExtraContent={
                <Tooltip title="各状态为当前账户总量，不随下方店铺、编号等筛选条件变化。刷新订单可重新获取。">
                  <span className="queue-scope">
                    {statisticsError ? '数量暂不可用' : '账户总量'}
                  </span>
                </Tooltip>
              }
              onChange={(key) =>
                setQuery({
                  ...query,
                  pageNum: 1,
                  statusList:
                    key === 'all'
                      ? undefined
                      : key === 'exceptions'
                        ? [92, 99]
                        : key.split(',').map(Number),
                })
              }
            />
            <OrderSearch
              filters={query}
              hasCustomStatus={
                !statusViews.some((item) => item.key === statusKey)
              }
              loading={loading}
              onSearch={(filters) =>
                setQuery({
                  ...filters,
                  pageNum: 1,
                  pageSize: query.pageSize,
                  sortBy: query.sortBy,
                  sortDirection: query.sortDirection,
                })
              }
              onReset={() => setQuery({ pageNum: 1, pageSize: query.pageSize })}
              onResetView={() => {
                setExpandedColumns(false);
                setQuery(readOrderQuery(new URLSearchParams()));
              }}
            />
            {error && (
              <Alert
                className="inline-error"
                type="error"
                showIcon
                message={error}
                description={
                  hasLoaded
                    ? '下方保留上次成功加载的结果，重试成功后可继续批量操作和导出。'
                    : '请检查连接后重试，筛选条件已保留。'
                }
                action={<Button onClick={refresh}>重试</Button>}
              />
            )}
            <div
              className={
                'table-toolbar order-toolbar' +
                (selectedOrders.length ? ' selection-bar' : '')
              }
            >
              <div className="order-toolbar-summary">
                {!showReceiptSummary && (
                  <div className="order-toolbar-status" aria-live="polite">
                    <span>
                      {selectedOrders.length ? (
                        <>
                          已选 <strong>{selectedOrders.length}</strong> 笔
                        </>
                      ) : loading ? (
                        '正在更新订单…'
                      ) : error ? (
                        '结果尚未更新'
                      ) : (
                        result.total + ' 条订单'
                      )}
                    </span>
                    <small className="muted">
                      {selectedOrders.length
                        ? '可转待拍单 ' +
                          validCount +
                          ' 笔 · 预计 ' +
                          validCount +
                          ' 积分'
                        : error
                          ? '批量操作暂不可用'
                          : !loading && result.total > 0
                            ? '当前页可多选'
                            : ''}
                    </small>
                  </div>
                )}
                {receipt && (
                  <OrderActionReceipt
                    receipt={receipt}
                    compact={!showReceiptSummary}
                    onDismiss={() => recordReceipt(null)}
                  />
                )}
              </div>
              {selectedOrders.length ? (
                <div className="order-toolbar-actions">
                  <Button type="text" onClick={() => setSelectedOrders([])}>
                    取消选择
                  </Button>
                  <Button
                    type="primary"
                    disabled={loading || Boolean(error) || !validCount}
                    onClick={() => setAction('update')}
                  >
                    转为待拍单
                  </Button>
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        { key: 'reset', label: '重置异常订单为待审核' },
                        { key: 'delete', label: '删除选中订单', danger: true },
                      ],
                      onClick: ({ key }) => {
                        setDeleteError('');
                        setAction(key);
                      },
                    }}
                  >
                    <Button
                      aria-label="更多批量操作"
                      icon={<MoreOutlined />}
                      disabled={loading || Boolean(error)}
                    />
                  </Dropdown>
                </div>
              ) : (
                <div className="order-toolbar-actions">
                  <Button
                    type="text"
                    className="table-fields-toggle"
                    icon={<TableOutlined />}
                    aria-pressed={expandedColumns}
                    onClick={() => setExpandedColumns((value) => !value)}
                  >
                    {expandedColumns ? '精简字段' : '全部字段'}
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    aria-label="刷新订单"
                    onClick={refresh}
                    loading={loading}
                  >
                    <span className="desktop-button-label">刷新</span>
                  </Button>
                  <Button
                    icon={<BarChartOutlined />}
                    aria-label="查看订单统计"
                    onClick={() => changeView('statistics')}
                  >
                    <span className="desktop-button-label">统计</span>
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={exporting}
                    disabled={loading || Boolean(error) || result.total === 0}
                    onClick={exportOrders}
                  >
                    导出结果
                  </Button>
                </div>
              )}
            </div>
            {loading && !hasLoaded ? (
              <Skeleton active={false} paragraph={{ rows: 8 }} title={false} />
            ) : (
              <OrderTable
                data={result.orders}
                loading={loading}
                hasError={Boolean(error)}
                query={query}
                pagination={{
                  current: query.pageNum,
                  pageSize: query.pageSize,
                  total: result.total,
                }}
                onChange={onTableChange}
                selectedRowKeys={selectedOrders.map((order) => order.id)}
                onSelectionChange={(_, rows) => setSelectedOrders(rows)}
                onImport={() => changeView('upload')}
                expandedColumns={expandedColumns}
                statusView={statusKey}
              />
            )}
          </>
        )}
        {view === 'upload' && (
          <ExcelUpload
            onViewOrders={() =>
              setUrlParams(
                writeOrderQuery({
                  pageNum: 1,
                  pageSize: query.pageSize,
                  statusList: [0],
                }),
              )
            }
            onFindOrder={(orderNo) =>
              setUrlParams(
                writeOrderQuery({
                  pageNum: 1,
                  pageSize: query.pageSize,
                  orderNo,
                }),
              )
            }
          />
        )}
        {view === 'statistics' && (
          <>
            <div className="table-toolbar">
              <span className="muted">账户订单统计</span>
              <Button
                onClick={refresh}
                icon={<ReloadOutlined />}
                loading={statisticsLoading}
              >
                刷新
              </Button>
            </div>
            {statisticsError ? (
              <Alert
                type="error"
                showIcon
                message={statisticsError}
                action={<Button onClick={refresh}>重试</Button>}
              />
            ) : statisticsLoading ? (
              <Skeleton active={false} />
            ) : (
              <div className="statistics-grid">
                {[
                  ['订单处理状态', statistics?.orderStatusCounts],
                  ['物流状态', statistics?.amazonLogisticsStatusCounts],
                ].map(([title, rows]) => (
                  <section key={title}>
                    <h3>{title}</h3>
                    {rows?.length ? (
                      rows.map((row) => (
                        <div className="stat-row" key={row.statusCode}>
                          <span>
                            {row.statusDesc ||
                              (title === '物流状态'
                                ? getLogisticsStatus({
                                    amzStatus: row.statusCode,
                                  }).text
                                : ORDER_STATUSES[row.statusCode]?.text ||
                                  '未知状态')}
                          </span>
                          <strong className="numeric">{row.count}</strong>
                        </div>
                      ))
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="暂无统计数据"
                      />
                    )}
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <BatchOrderUpdate
        visible={action === 'update'}
        onClose={() => setAction(null)}
        onSuccess={onActionSuccess}
        onReceipt={recordReceipt}
        pointsBalance={pointsLoading ? null : pointsBalance}
        selectedOrders={selectedOrders}
      />
      <BatchOrderReset
        visible={action === 'reset'}
        onClose={() => setAction(null)}
        onSuccess={onActionSuccess}
        selectedOrders={selectedOrders}
      />
      <Modal
        title="删除选中订单"
        open={action === 'delete'}
        onOk={deleteOrders}
        onCancel={() => {
          if (!deleting) setAction(null);
        }}
        okText="确认删除"
        cancelText="保留订单"
        confirmLoading={deleting}
        okButtonProps={{ danger: true, disabled: !selectedOrders.length }}
        cancelButtonProps={{ disabled: deleting }}
        closable={!deleting}
        maskClosable={!deleting}
        keyboard={!deleting}
      >
        <p>
          将删除选中的 <strong>{selectedOrders.length}</strong>{' '}
          条订单，此操作无法撤销。
        </p>
        {deleteError && <Alert type="error" showIcon message={deleteError} />}
      </Modal>
    </section>
  );
};
const OrderManagement = () => {
  const [params] = useSearchParams();
  const { recentQuery } = useOrderWorkspace();
  if (!params.toString()) {
    return (
      <Navigate
        to={{
          pathname: '/app/orders',
          search: '?' + writeOrderQuery(recentQuery).toString(),
        }}
        replace
      />
    );
  }
  return <OrderWorkspace />;
};
export default OrderManagement;
